﻿package br.com.htms.inspeccaocampo.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.NavigationBar
import androidx.compose.material3.NavigationBarItem
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import br.com.htms.inspeccaocampo.auth.AuthApi
import br.com.htms.inspeccaocampo.data.FormTipo
import br.com.htms.inspeccaocampo.form.AppController
import br.com.htms.inspeccaocampo.form.AppTab
import br.com.htms.inspeccaocampo.platform.platformName
import br.com.htms.inspeccaocampo.shared.ui.components.CenteredAppDialog
import br.com.htms.inspeccaocampo.shared.ui.components.FormFieldDefaults
import br.com.htms.inspeccaocampo.ui.screens.ConfigHeaderActions
import br.com.htms.inspeccaocampo.ui.screens.SharedChecklistAlojamentoScreen
import br.com.htms.inspeccaocampo.ui.screens.SharedComunicacaoOcorrenciasScreen
import br.com.htms.inspeccaocampo.ui.screens.SharedConfigScreen
import br.com.htms.inspeccaocampo.ui.screens.SharedFormHubScreen
import br.com.htms.inspeccaocampo.ui.screens.SharedHistoricoScreen
import br.com.htms.inspeccaocampo.ui.screens.SharedLoginScreen
import br.com.htms.inspeccaocampo.ui.screens.SharedRelatorioAlojamentoScreen
import br.com.htms.inspeccaocampo.ui.screens.SharedRelatorioFotoScreen

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SharedAppNav(controller: AppController) {
    val tab by controller.tab.collectAsState()
    val appConfig by controller.appConfig.collectAsState()
    val mensagem by controller.mensagem.collectAsState()
    val rascunho by controller.rascunhoRestaurado.collectAsState()
    val authReady by controller.authReady.collectAsState()
    val authUser by controller.authUser.collectAsState()
    val snackbar = remember { SnackbarHostState() }
    val isWeb = platformName() == "Web"

    var mostrarSenha by remember { mutableStateOf(false) }
    var senha by remember { mutableStateOf("") }
    var erroSenha by remember { mutableStateOf(false) }
    val isAdmin = authUser?.isAdmin == true
    val configHeaderActions = remember { ConfigHeaderActions() }
    val formTipoAtivo by controller.formTipoAtivo.collectAsState()

    LaunchedEffect(mensagem) {
        val msg = mensagem ?: return@LaunchedEffect
        snackbar.showSnackbar(msg)
        controller.consumirMensagem()
    }

    LaunchedEffect(rascunho) {
        if (rascunho) {
            snackbar.showSnackbar("Rascunho restaurado — seus dados foram recuperados.")
            controller.consumirAvisoRascunho()
        }
    }

    if (!authReady) {
        if (isWeb) {
            WebCenteredColumn(transparentCenter = true) {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Text("Verificando sessão...", color = Color.White)
                }
            }
        } else {
            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                Text("Verificando sessão...")
            }
        }
        return
    }

    if (controller.loginRequired && authUser == null) {
        val loginContent: @Composable () -> Unit = {
            SharedLoginScreen(
                onLoggedIn = {
                    AuthApi.currentSession()?.let { controller.onLoginSuccess(it) }
                },
                embeddedInShell = isWeb
            )
        }
        if (isWeb) {
            WebCenteredColumn(transparentCenter = true, content = loginContent)
        } else {
            loginContent()
        }
        return
    }

    if (mostrarSenha) {
        CenteredAppDialog(
            onDismissRequest = {
                mostrarSenha = false
                senha = ""
                erroSenha = false
            },
            title = "Configuração",
            confirmLabel = "Entrar",
            onConfirm = {
                if (controller.tentarAbrirConfig(senha)) {
                    mostrarSenha = false
                    senha = ""
                    erroSenha = false
                } else {
                    erroSenha = true
                }
            },
            dismissLabel = "Cancelar"
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text("Digite a senha para abrir configuração e modo treinamento.")
                OutlinedTextField(
                    value = senha,
                    onValueChange = {
                        senha = it
                        erroSenha = false
                    },
                    label = { Text("Senha") },
                    modifier = Modifier.fillMaxWidth(),
                    isError = erroSenha,
                    visualTransformation = PasswordVisualTransformation(),
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.NumberPassword),
                    colors = FormFieldDefaults.textFieldColors()
                )
                if (erroSenha) {
                    Text("Senha incorreta.", color = MaterialTheme.colorScheme.error)
                }
            }
        }
    }

    val appScaffold: @Composable () -> Unit = {
        Scaffold(
            modifier = Modifier.fillMaxSize(),
            containerColor = MaterialTheme.colorScheme.background,
            topBar = {
                TopAppBar(
                    title = {
                        Column {
                            Text(appConfig.nomeApp, fontWeight = FontWeight.Bold)
                            val formLabel = when (tab) {
                                AppTab.FORM -> formTipoAtivo.rotulo
                                else -> appConfig.nomeInspecao
                            }
                            val sub = buildString {
                                append(formLabel)
                                authUser?.let {
                                    append(" · ")
                                    append(it.username)
                                    if (it.isAdmin) append(" (admin)")
                                }
                            }
                            Text(sub, style = MaterialTheme.typography.bodySmall)
                        }
                    },
                    actions = {
                        if (tab == AppTab.CONFIG) {
                            TextButton(onClick = { configHeaderActions.onVoltar?.invoke() }) {
                                Text("Voltar")
                            }
                            TextButton(onClick = { configHeaderActions.onSalvar?.invoke() }) {
                                Text("Salvar / Aplicar")
                            }
                        }
                        if (authUser != null) {
                            TextButton(onClick = { controller.logout() }) {
                                Text("Sair")
                            }
                        }
                        TextButton(
                            onClick = {
                                if (isAdmin) {
                                    controller.abrirConfigComoAdmin()
                                } else {
                                    mostrarSenha = true
                                }
                            }
                        ) {
                            Text("Config")
                        }
                    },
                    colors = TopAppBarDefaults.topAppBarColors(
                        containerColor = MaterialTheme.colorScheme.primaryContainer
                    )
                )
            },
            bottomBar = {
                if (tab != AppTab.CONFIG) {
                    NavigationBar(
                        containerColor = MaterialTheme.colorScheme.surfaceVariant
                    ) {
                        NavigationBarItem(
                            selected = tab == AppTab.HUB || tab == AppTab.FORM,
                            onClick = { controller.irPara(AppTab.HUB) },
                            icon = { Text("F", fontWeight = FontWeight.Bold) },
                            label = { Text("Formulários") }
                        )
                        NavigationBarItem(
                            selected = tab == AppTab.HISTORICO,
                            onClick = { controller.irPara(AppTab.HISTORICO) },
                            icon = { Text("H", fontWeight = FontWeight.Bold) },
                            label = { Text("Histórico") }
                        )
                    }
                }
            },
            snackbarHost = { SnackbarHost(snackbar) }
        ) { padding ->
            Column(Modifier.padding(padding).fillMaxSize()) {
                when (tab) {
                    AppTab.HUB -> SharedFormHubScreen(controller = controller)
                    AppTab.FORM -> {
                        when (formTipoAtivo) {
                            FormTipo.INSPECAO_CAMPO ->
                                SharedFormScreen(controller = controller, embedded = true)
                            FormTipo.RELATORIO_FOTO_ALOJAMENTO ->
                                SharedRelatorioFotoScreen(controller = controller)
                            FormTipo.CHECKLIST_ALOJAMENTO ->
                                SharedChecklistAlojamentoScreen(controller = controller)
                            FormTipo.RELATORIO_ALOJAMENTO ->
                                SharedRelatorioAlojamentoScreen(controller = controller)
                            FormTipo.COMUNICACAO_OCORRENCIAS ->
                                SharedComunicacaoOcorrenciasScreen(controller = controller)
                        }
                    }
                    AppTab.HISTORICO -> SharedHistoricoScreen(controller = controller, snackbar = snackbar)
                    AppTab.CONFIG -> SharedConfigScreen(
                        controller = controller,
                        snackbar = snackbar,
                        headerActions = configHeaderActions,
                        onVoltar = { controller.voltarDaConfig() }
                    )
                }
            }
        }
    }

    if (!isWeb) {
        appScaffold()
        return
    }

    WebCenteredColumn(content = appScaffold)
}

/** Web: fundo HTMS + coluna central (~60%) — login e app usam a mesma proporção. */
@Composable
private fun WebCenteredColumn(
    transparentCenter: Boolean = false,
    content: @Composable () -> Unit
) {
    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        HtmsBackdrop(modifier = Modifier.fillMaxSize())
        val columnWidth = if (maxWidth >= 900.dp) {
            (maxWidth * 0.6f).coerceIn(520.dp, 1100.dp)
        } else {
            maxWidth
        }
        Box(
            modifier = Modifier
                .width(columnWidth)
                .fillMaxHeight()
                .align(Alignment.Center)
                .then(
                    if (transparentCenter) Modifier
                    else Modifier.background(MaterialTheme.colorScheme.background)
                )
        ) {
            content()
        }
    }
}
